import { create } from 'zustand';
import api from '../lib/api';
import type {
  Task,
  TaskCreate,
  TaskUpdate,
  TaskFilters,
  PaginationMeta,
  GroupedTasks,
} from '../types';

interface TaskState {
  // List state
  tasks: Task[];
  pagination: PaginationMeta | null;
  filters: TaskFilters;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;

  // Person tasks state (for detail page)
  personTasks: Task[];
  personTasksPagination: PaginationMeta | null;
  currentPersonId: string | null;
  personTasksLoading: boolean;
  personTasksError: string | null;

  // Single task state
  currentTask: Task | null;
  taskLoading: boolean;
  taskError: string | null;

  // Create/Update/Delete state
  isSaving: boolean;
  saveError: string | null;

  // Actions - List
  fetchTasks: (page?: number) => Promise<void>;
  setFilters: (filters: Partial<TaskFilters>) => void;
  setPage: (page: number) => void;
  clearFilters: () => void;

  // Actions - Person Tasks
  fetchPersonTasks: (personId: string, page?: number, includeCompleted?: boolean) => Promise<void>;
  clearPersonTasks: () => void;

  // Actions - Single Task
  fetchTask: (id: string) => Promise<void>;
  clearCurrentTask: () => void;

  // Actions - CRUD
  createTask: (data: TaskCreate) => Promise<Task>;
  updateTask: (id: string, data: TaskUpdate) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
  completeTask: (id: string) => Promise<Task>;
  cancelTask: (id: string) => Promise<Task>;

  // Actions - Utility
  clearError: () => void;
  clearSaveError: () => void;
  getGroupedTasks: () => GroupedTasks;
}

// Helper to group tasks by due date
function groupTasksByDueDate(tasks: Task[]): GroupedTasks {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay())); // End of current week (Sunday)

  const groups: GroupedTasks = {
    overdue: [],
    today: [],
    thisWeek: [],
    later: [],
  };

  for (const task of tasks) {
    const dueDate = new Date(task.dueDate);
    const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

    if (dueDateOnly < today) {
      groups.overdue.push(task);
    } else if (dueDateOnly.getTime() === today.getTime()) {
      groups.today.push(task);
    } else if (dueDateOnly <= endOfWeek) {
      groups.thisWeek.push(task);
    } else {
      groups.later.push(task);
    }
  }

  return groups;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  // Initial state - List
  tasks: [],
  pagination: null,
  filters: {},
  currentPage: 1,
  pageSize: 25,
  isLoading: false,
  error: null,

  // Initial state - Person Tasks
  personTasks: [],
  personTasksPagination: null,
  currentPersonId: null,
  personTasksLoading: false,
  personTasksError: null,

  // Initial state - Single Task
  currentTask: null,
  taskLoading: false,
  taskError: null,

  // Initial state - CRUD
  isSaving: false,
  saveError: null,

  // ==================== LIST ACTIONS ====================

  /**
   * Fetch tasks with current filters and pagination
   */
  fetchTasks: async (page?: number) => {
    const { filters, currentPage, pageSize } = get();
    const pageToFetch = page ?? currentPage;

    try {
      set({ isLoading: true, error: null });

      const params = new URLSearchParams();
      params.append('page', pageToFetch.toString());
      params.append('limit', pageSize.toString());

      // Apply filters
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.assignedUserId) {
        params.append('assignedUserId', filters.assignedUserId);
      }
      if (filters.personId) {
        params.append('personId', filters.personId);
      }
      if (filters.dueBefore) {
        params.append('dueBefore', filters.dueBefore);
      }
      if (filters.dueAfter) {
        params.append('dueAfter', filters.dueAfter);
      }
      if (filters.includeCompleted) {
        params.append('includeCompleted', 'true');
      }

      const response = await api.get(`/api/tasks?${params.toString()}`);

      set({
        tasks: response.data.tasks,
        pagination: response.data.pagination,
        currentPage: pageToFetch,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch tasks:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load tasks',
      });
    }
  },

  /**
   * Update filters and refetch
   */
  setFilters: (newFilters: Partial<TaskFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      currentPage: 1, // Reset to first page on filter change
    }));
    get().fetchTasks(1);
  },

  /**
   * Set page and refetch
   */
  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchTasks(page);
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({ filters: {}, currentPage: 1 });
    get().fetchTasks(1);
  },

  // ==================== PERSON TASKS ACTIONS ====================

  /**
   * Fetch tasks for a specific person
   */
  fetchPersonTasks: async (personId: string, page?: number, includeCompleted?: boolean) => {
    try {
      set({ personTasksLoading: true, personTasksError: null, currentPersonId: personId });

      const params = new URLSearchParams();
      params.append('page', (page ?? 1).toString());
      params.append('limit', '25');
      if (includeCompleted) {
        params.append('includeCompleted', 'true');
      }

      const response = await api.get(
        `/api/people/${personId}/tasks?${params.toString()}`
      );

      set({
        personTasks: response.data.tasks,
        personTasksPagination: response.data.pagination,
        personTasksLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch person tasks:', error);
      set({
        personTasksLoading: false,
        personTasksError: error.response?.data?.message || 'Failed to load tasks',
      });
    }
  },

  /**
   * Clear person tasks (when navigating away)
   */
  clearPersonTasks: () => {
    set({
      personTasks: [],
      personTasksPagination: null,
      currentPersonId: null,
      personTasksError: null,
    });
  },

  // ==================== SINGLE TASK ACTIONS ====================

  /**
   * Fetch a single task by ID
   */
  fetchTask: async (id: string) => {
    try {
      set({ taskLoading: true, taskError: null });

      const response = await api.get(`/api/tasks/${id}`);

      set({
        currentTask: response.data,
        taskLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch task:', error);
      set({
        taskLoading: false,
        taskError: error.response?.data?.message || 'Failed to load task',
      });
    }
  },

  /**
   * Clear current task
   */
  clearCurrentTask: () => {
    set({ currentTask: null, taskError: null });
  },

  // ==================== CRUD ACTIONS ====================

  /**
   * Create a new task
   */
  createTask: async (data: TaskCreate) => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.post('/api/tasks', data);
      const newTask = response.data;

      // Add to list if it would be visible with current filters
      set((state) => {
        const { filters } = state;
        const shouldAddToList =
          !filters.status || filters.status === 'PENDING';

        return {
          tasks: shouldAddToList ? [newTask, ...state.tasks] : state.tasks,
          pagination: shouldAddToList && state.pagination
            ? { ...state.pagination, totalCount: state.pagination.totalCount + 1 }
            : state.pagination,
          isSaving: false,
        };
      });

      // If task is linked to a person and we're viewing that person's tasks
      const { currentPersonId } = get();
      if (data.personId && data.personId === currentPersonId) {
        set((state) => ({
          personTasks: [newTask, ...state.personTasks],
          personTasksPagination: state.personTasksPagination
            ? { ...state.personTasksPagination, totalCount: state.personTasksPagination.totalCount + 1 }
            : null,
        }));
      }

      return newTask;
    } catch (error: any) {
      console.error('Failed to create task:', error);
      const message = error.response?.data?.message || 'Failed to create task';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Update a task
   */
  updateTask: async (id: string, data: TaskUpdate) => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.patch(`/api/tasks/${id}`, data);
      const updatedTask = response.data;

      // Update in lists
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updatedTask : t)),
        personTasks: state.personTasks.map((t) => (t.id === id ? updatedTask : t)),
        currentTask: state.currentTask?.id === id ? updatedTask : state.currentTask,
        isSaving: false,
      }));

      return updatedTask;
    } catch (error: any) {
      console.error('Failed to update task:', error);
      const message = error.response?.data?.message || 'Failed to update task';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Delete a task
   */
  deleteTask: async (id: string) => {
    try {
      set({ isSaving: true, saveError: null });

      await api.delete(`/api/tasks/${id}`);

      // Remove from lists
      set((state) => ({
        tasks: state.tasks.filter((t) => t.id !== id),
        personTasks: state.personTasks.filter((t) => t.id !== id),
        pagination: state.pagination
          ? { ...state.pagination, totalCount: state.pagination.totalCount - 1 }
          : null,
        personTasksPagination: state.personTasksPagination
          ? { ...state.personTasksPagination, totalCount: state.personTasksPagination.totalCount - 1 }
          : null,
        isSaving: false,
      }));
    } catch (error: any) {
      console.error('Failed to delete task:', error);
      const message = error.response?.data?.message || 'Failed to delete task';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Mark a task as complete
   */
  completeTask: async (id: string) => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.post(`/api/tasks/${id}/complete`);
      const updatedTask = response.data;

      // Update or remove from lists depending on filter
      set((state) => {
        const { filters } = state;
        const shouldRemove = !filters.includeCompleted;

        return {
          tasks: shouldRemove
            ? state.tasks.filter((t) => t.id !== id)
            : state.tasks.map((t) => (t.id === id ? updatedTask : t)),
          personTasks: shouldRemove
            ? state.personTasks.filter((t) => t.id !== id)
            : state.personTasks.map((t) => (t.id === id ? updatedTask : t)),
          currentTask: state.currentTask?.id === id ? updatedTask : state.currentTask,
          isSaving: false,
        };
      });

      return updatedTask;
    } catch (error: any) {
      console.error('Failed to complete task:', error);
      const message = error.response?.data?.message || 'Failed to complete task';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Cancel a task
   */
  cancelTask: async (id: string) => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.post(`/api/tasks/${id}/cancel`);
      const updatedTask = response.data;

      // Update or remove from lists depending on filter
      set((state) => {
        const { filters } = state;
        const shouldRemove = !filters.includeCompleted;

        return {
          tasks: shouldRemove
            ? state.tasks.filter((t) => t.id !== id)
            : state.tasks.map((t) => (t.id === id ? updatedTask : t)),
          personTasks: shouldRemove
            ? state.personTasks.filter((t) => t.id !== id)
            : state.personTasks.map((t) => (t.id === id ? updatedTask : t)),
          currentTask: state.currentTask?.id === id ? updatedTask : state.currentTask,
          isSaving: false,
        };
      });

      return updatedTask;
    } catch (error: any) {
      console.error('Failed to cancel task:', error);
      const message = error.response?.data?.message || 'Failed to cancel task';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  // ==================== UTILITY ACTIONS ====================

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Clear save error state
   */
  clearSaveError: () => {
    set({ saveError: null });
  },

  /**
   * Get tasks grouped by due date (for My Tasks page)
   */
  getGroupedTasks: () => {
    const { tasks } = get();
    return groupTasksByDueDate(tasks);
  },
}));
